import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  where,
  getDocs,
  updateDoc,
  query,
  Timestamp,
  deleteDoc
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from '../images/hotel_logo.png';
import { HOTEL_NAME } from "../Variables.js";

Modal.setAppElement('#root');

// Date formatters
const dateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

const formatDateTime = (date) => {
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};

const formatDateForFilename = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Natural sort for rate plan codes
const naturalSort = (a, b) => {
  const codeA = String(a.code ?? '').trim();
  const codeB = String(b.code ?? '').trim();
  return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
};

const RatePlans = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [ratePlans, setRatePlans] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [filteredRatePlans, setFilteredRatePlans] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [newRatePlan, setNewRatePlan] = useState({
    name: '',
    code: '',
    description: '',
    cancellationPolicy: 'Flexible',
    mealPlan: 'Room Only',
    rateAdjustment: 0,
    rateAdjustmentType: 'fixed',
    applicableRoomTypeIds: [],
    active: true
  });
  const [editingRatePlan, setEditingRatePlan] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [currentRatePlan, setCurrentRatePlan] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('excel');

  const ratePlansCollectionRef = collection(db, "ratePlans");
  const roomTypesCollectionRef = collection(db, "roomTypes");
  const hotelName = `${HOTEL_NAME}`;

  const allowedRoles = ['Admin', 'Supreme', 'Cashier', 'Front Desk'];

  useEffect(() => {
    if (user && userData) {
      fetchRoomTypes();
      fetchRatePlans();
    }
  }, [user, userData]);

  useEffect(() => {
    const filtered = ratePlans.filter((plan) =>
      plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (plan.code && plan.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (plan.description && plan.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredRatePlans(filtered.sort(naturalSort));
  }, [searchTerm, ratePlans]);

  const fetchRoomTypes = async () => {
    try {
      const q = query(roomTypesCollectionRef);
      const snapshot = await getDocs(q);
      const types = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRoomTypes(types.filter(t => t.active !== false));
    } catch (err) {
      console.error("Error fetching room types:", err);
    }
  };

  const fetchRatePlans = async () => {
    if (!user?.uid) return;

    setIsFetching(true);
    try {
      const q = query(ratePlansCollectionRef);
      const snapshot = await getDocs(q);
      const planList = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));

      const enriched = await Promise.all(planList.map(async (plan) => {
        if (plan.applicableRoomTypeIds && plan.applicableRoomTypeIds.length > 0) {
          const typeQueries = plan.applicableRoomTypeIds.map(id =>
            getDocs(query(roomTypesCollectionRef, where("__name__", "==", id)))
          );
          const typeSnapshots = await Promise.all(typeQueries);
          const names = typeSnapshots
            .flatMap(snap => snap.docs)
            .map(doc => doc.data().name)
            .filter(Boolean)
            .join(', ');
          return { ...plan, applicableRoomTypesDisplay: names || 'All' };
        }
        return { ...plan, applicableRoomTypesDisplay: 'All' };
      }));

      setRatePlans(enriched);
      setHasFetched(true);
    } catch (err) {
      console.error("Error fetching rate plans:", err);
      setError("Failed to fetch rate plans");
      setShowModal(true);
    } finally {
      setIsFetching(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRatePlan({ ...newRatePlan, [name]: value });
  };

  const handleRoomTypeCheckbox = (roomTypeId) => {
    setNewRatePlan(prev => {
      const ids = prev.applicableRoomTypeIds || [];
      if (ids.includes(roomTypeId)) {
        return { ...prev, applicableRoomTypeIds: ids.filter(id => id !== roomTypeId) };
      } else {
        return { ...prev, applicableRoomTypeIds: [...ids, roomTypeId] };
      }
    });
  };

  const handleAddRatePlan = async () => {
    if (!newRatePlan.name.trim()) {
      setError("Rate plan name is required");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      await addDoc(ratePlansCollectionRef, {
        ...newRatePlan,
        rateAdjustment: Number(newRatePlan.rateAdjustment),
        userId: user?.uid,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Rate plan added successfully");
      fetchRatePlans();
      resetForm();
      setIsAddModalOpen(false);
    } catch (err) {
      setError("Failed to add rate plan");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateRatePlan = async () => {
    setUpdate(true);
    try {
      const planDoc = doc(db, "ratePlans", editingRatePlan.id);
      await updateDoc(planDoc, {
        ...newRatePlan,
        rateAdjustment: Number(newRatePlan.rateAdjustment)
      });
      setSuccessMessage("Rate plan updated successfully");
      fetchRatePlans();
      resetForm();
      setEditingRatePlan(null);
      setIsEditModalOpen(false);
    } catch (err) {
      setError("Failed to update rate plan");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const resetForm = () => {
    setNewRatePlan({
      name: '',
      code: '',
      description: '',
      cancellationPolicy: 'Flexible',
      mealPlan: 'Room Only',
      rateAdjustment: 0,
      rateAdjustmentType: 'fixed',
      applicableRoomTypeIds: [],
      active: true
    });
  };

  const handleFileChange = (e) => setFile(e.target.files[0]);
  const handleFileTypeChange = (e) => setFileType(e.target.value);

  const processBulkImport = async () => {
    if (!file) {
      setError("Please select a file");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      let dataArray = [];
      if (fileType === 'excel') {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer);
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        dataArray = XLSX.utils.sheet_to_json(sheet);
      } else {
        const text = await file.text();
        const result = Papa.parse(text, { header: true, skipEmptyLines: true });
        dataArray = result.data;
      }

      if (dataArray.length === 0) throw new Error("No data found");

      let successCount = 0;
      let errorCount = 0;

      for (let item of dataArray) {
        try {
          if (!item.name) { errorCount++; continue; }

          await addDoc(ratePlansCollectionRef, {
            name: item.name.trim(),
            code: String(item.code ?? '').trim(),
            description: item.description || '',
            cancellationPolicy: item.cancellationPolicy || 'Flexible',
            mealPlan: item.mealPlan || 'Room Only',
            rateAdjustment: Number(item.rateAdjustment) || 0,
            rateAdjustmentType: ['fixed', 'percentage'].includes(item.rateAdjustmentType) ? item.rateAdjustmentType : 'fixed',
            applicableRoomTypeIds: item.applicableRoomTypeIds ? item.applicableRoomTypeIds.split(',').map(s => s.trim()) : [],
            active: item.active !== undefined ? Boolean(item.active) : true,
            userId: user?.uid,
            createdAt: Timestamp.now()
          });
          successCount++;
        } catch (err) {
          errorCount++;
        }
      }

      setSuccessMessage(`Bulk import complete: ${successCount} rate plans added`);
      fetchRatePlans();
    } catch (err) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setUpdate(false);
      setFile(null);
      setIsBulkImportModalOpen(false);
      setShowModal(true);
    }
  };

  const generateRatePlansPDF = () => {
    if (ratePlans.length === 0) {
      setError("No rate plans to export");
      setShowModal(true);
      return;
    }

    const doc = new jsPDF();
    const fullName = userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";

    let y = 10;
    doc.addImage(Logo, "PNG", 14, y, 15, 15);
    doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.text(hotelName, 35, y + 5);
    doc.setFontSize(16); doc.setFont("helvetica", "normal"); doc.text("Rate Plans Report", 35, y + 13);
    doc.setFontSize(10);
    doc.text(`Generated by: ${fullName}`, 35, y + 18);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, y + 23);
    y += 28;

    const sorted = [...ratePlans].sort(naturalSort);
    const tableData = sorted.map((plan, i) => [
      (i + 1).toString(),
      plan.code || '-',
      plan.name,
      plan.mealPlan,
      plan.cancellationPolicy,
      `${plan.rateAdjustmentType === 'percentage' ? plan.rateAdjustment + '%' : '₦' + plan.rateAdjustment.toLocaleString()}`,
      plan.applicableRoomTypesDisplay || 'All',
      plan.active ? 'Active' : 'Inactive',
      plan.createdAt ? dateFormatter.format(plan.createdAt.toDate()) : '-'
    ]);

    autoTable(doc, {
      startY: y,
      head: [['S/N', 'Code', 'Name', 'Meal Plan', 'Cancellation', 'Adjustment', 'Room Types', 'Status', 'Created']],
      body: tableData,
      theme: 'striped',
      margin: { left: 4, right: 10 },
      styles: { fontSize: 8, textColor: 0 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 12 },
        1: { cellWidth: 15 },
        2: { cellWidth: 33 },
        3: { cellWidth: 23 },
        4: { cellWidth: 23 },
        5: { cellWidth: 23 },
        6: { cellWidth: 33 },
        7: { cellWidth: 20 },
        8: { cellWidth: 20 }
      }
    });

    doc.save(`rate_plans_${formatDateForFilename(new Date())}.pdf`);
  };

  const handleEdit = (plan) => {
    setEditingRatePlan(plan);
    setNewRatePlan({
      name: plan.name,
      code: plan.code || '',
      description: plan.description || '',
      cancellationPolicy: plan.cancellationPolicy || 'Flexible',
      mealPlan: plan.mealPlan || 'Room Only',
      rateAdjustment: plan.rateAdjustment || 0,
      rateAdjustmentType: plan.rateAdjustmentType || 'fixed',
      applicableRoomTypeIds: plan.applicableRoomTypeIds || [],
      active: plan.active
    });
    setIsEditModalOpen(true);
  };

  const confirmDelete = (plan) => {
    setCurrentRatePlan(plan);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async (id) => {
    setUpdate(true);
    try {
      await deleteDoc(doc(db, "ratePlans", id));
      setSuccessMessage("Rate plan deleted successfully");
      fetchRatePlans();
    } catch (err) {
      console.error("Error deleting rate plan:", err);
      setError("Failed to delete rate plan");
    } finally {
      setUpdate(false);
      setIsDeleteModalOpen(false);
      setShowModal(true);
    }
  };

  if (loading) return <Loader text={"Loading"} />;
  if (!userData) return <Loader text={"Reading"} />;

  const displayContent = () => {
    if (isFetching && !hasFetched) return <p className="loading-text">Loading rate plans...</p>;

    if (filteredRatePlans.length === 0) {
      return searchTerm
        ? <p className="loading-text">No rate plans match your search.</p>
        : <p className="loading-text">No rate plans added yet. Create your first rate plan.</p>;
    }

    return filteredRatePlans.map((plan) => (
      <div key={plan.id} className="class-item">
        <div className="column gap-1">
          <p><strong>{plan.name}</strong><br />
            <span>Code: {plan.code || '—'}</span>&nbsp;-&nbsp;
            <span>{plan.mealPlan}</span><br />
            <span>Cancellation: {plan.cancellationPolicy}</span><br />
            <span>Adjustment: {plan.rateAdjustmentType === 'percentage' ? plan.rateAdjustment + '%' : '₦' + plan.rateAdjustment.toLocaleString()}</span><br />
            <span>Applies to: {plan.applicableRoomTypesDisplay || 'All Room Types'}</span><br />
            <span className="size5 font-gold bold">{plan.active ? 'Active' : 'Not active'}</span>
          </p>
        </div>
        <div className="row align-center justify-center gap-15">
          <button className="list-btn" onClick={() => handleEdit(plan)}>
            <i className="fas fa-edit"></i>
          </button>
          <button className="list-btn delete-btn" onClick={() => confirmDelete(plan)}>
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>
    ));
  };

  return (
    <>
      {update && <Loader text={"Processing"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Rate Plans</h2>
            <Link to="#" className="home-link" onClick={fetchRatePlans}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed4">
            <div className="row justify-center align-center gap-15 add-buttons add-buttons1 margin-top-cashflow1">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Rate Plan</button>
              <button className="list-btn" onClick={generateRatePlansPDF}><i className="fas fa-print"></i></button>
              <button className="login-button login-button-d caps" onClick={() => setIsBulkImportModalOpen(true)}>Bulk Import</button>
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by name, code, or description"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {displayContent()}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => { setError(""); setSuccessMessage(""); setShowModal(false); }}
        />
      )}

      {/* Add Modal */}
      <Modal isOpen={isAddModalOpen} onRequestClose={() => { setIsAddModalOpen(false); resetForm(); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Add Rate Plan</h2>
        <form className="session-form modal-scroll">
          <div className="desktop-form-input">
            <div>
              <label className="input-label">Name</label>
              <input type="text" name="name" className="input-field" placeholder="e.g., Non-refundable Rate" value={newRatePlan.name} onChange={handleInputChange} required />
            </div>
            <div>
              <label className="input-label">Code (Optional)</label>
              <input type="text" name="code" className="input-field" placeholder="e.g., NR" value={newRatePlan.code} onChange={handleInputChange} />
            </div>
          </div>
          <label className="input-label">Description</label>
          <textarea name="description" className="input-field" rows="2" value={newRatePlan.description} onChange={handleInputChange} />

          <div className="desktop-form-input">
            <div>
              <label className="input-label">Meal Plan</label>
              <select name="mealPlan" className="input-field" value={newRatePlan.mealPlan} onChange={handleInputChange}>
                <option value="Room Only">Room Only</option>
                <option value="Breakfast Included">Breakfast Included</option>
                <option value="Half Board">Half Board</option>
                <option value="Full Board">Full Board</option>
                <option value="All Inclusive">All Inclusive</option>
              </select>
            </div>
            <div>
              <label className="input-label">Cancellation Policy</label>
              <select name="cancellationPolicy" className="input-field" value={newRatePlan.cancellationPolicy} onChange={handleInputChange}>
                <option value="Flexible">Flexible</option>
                <option value="Non-refundable">Non-refundable</option>
                <option value="24 Hours">24 Hours Prior</option>
                <option value="48 Hours">48 Hours Prior</option>
              </select>
            </div>
          </div>
          <label className="input-label">Rate Adjustment</label>
          <div className="row gap-1">
            <select name="rateAdjustmentType" className="input-field" style={{width: '100px'}} value={newRatePlan.rateAdjustmentType} onChange={handleInputChange}>
              <option value="fixed">Fixed ₦</option>
              <option value="percentage">Percentage %</option>
            </select>
            <input type="number" name="rateAdjustment" className="input-field" placeholder="0" value={newRatePlan.rateAdjustment} onChange={handleInputChange} />
          </div>

          <label className="input-label">Applicable Room Types (Leave blank for all)</label>
          <div className="column gap-05" style={{maxHeight: '150px', overflowY: 'auto', padding: '8px', border: '1px solid #464646', borderRadius: '4px'}}>
            {roomTypes.length === 0 ? <p>No room types available</p> : roomTypes.map(type => (
              <label key={type.id} style={{display: 'block'}}>
                <input
                  type="checkbox"
                  className='gold-tick'
                  checked={newRatePlan.applicableRoomTypeIds.includes(type.id)}
                  onChange={() => handleRoomTypeCheckbox(type.id)}
                />
                {type.name}
              </label>
            ))}
          </div>

          <div className="row align-center gap-1">
            <button type="button" className="login-button" onClick={() => { setIsAddModalOpen(false); resetForm(); }}>Cancel</button>
            <button type="button" className="login-button" onClick={handleAddRatePlan}>Add Rate Plan</button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} onRequestClose={() => { setIsEditModalOpen(false); resetForm(); setEditingRatePlan(null); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Edit Rate Plan</h2>
        <form className="session-form modal-scroll">
          <div className="desktop-form-input">
            <div>
              <label className="input-label">Name</label>
              <input type="text" name="name" className="input-field" value={newRatePlan.name} onChange={handleInputChange} required />
            </div>
            <div>
              <label className="input-label">Code</label>
              <input type="text" name="code" className="input-field" value={newRatePlan.code} onChange={handleInputChange} />
            </div>
          </div>
          <label className="input-label">Description</label>
          <textarea name="description" className="input-field" rows="2" value={newRatePlan.description} onChange={handleInputChange} />

          <div className="desktop-form-input">
            <div>
              <label className="input-label">Meal Plan</label>
              <select name="mealPlan" className="input-field" value={newRatePlan.mealPlan} onChange={handleInputChange}>
                <option value="Room Only">Room Only</option>
                <option value="Breakfast Included">Breakfast Included</option>
                <option value="Half Board">Half Board</option>
                <option value="Full Board">Full Board</option>
                <option value="All Inclusive">All Inclusive</option>
              </select>
            </div>
            <div>
              <label className="input-label">Cancellation Policy</label>
              <select name="cancellationPolicy" className="input-field" value={newRatePlan.cancellationPolicy} onChange={handleInputChange}>
                <option value="Flexible">Flexible</option>
                <option value="Non-refundable">Non-refundable</option>
                <option value="24 Hours">24 Hours Prior</option>
                <option value="48 Hours">48 Hours Prior</option>
              </select>
            </div>
          </div>
          <label className="input-label">Rate Adjustment</label>
          <div className="row gap-1">
            <select name="rateAdjustmentType" className="input-field" style={{width: '100px'}} value={newRatePlan.rateAdjustmentType} onChange={handleInputChange}>
              <option value="fixed">Fixed ₦</option>
              <option value="percentage">Percentage %</option>
            </select>
            <input type="number" name="rateAdjustment" className="input-field" value={newRatePlan.rateAdjustment} onChange={handleInputChange} />
          </div>

          <label className="input-label">Applicable Room Types</label>
          <div className="column gap-05" style={{maxHeight: '150px', overflowY: 'auto', padding: '8px', border: '1px solid #464646', borderRadius: '4px'}}>
            {roomTypes.map(type => (
              <label key={type.id} style={{display: 'block'}}>
                <input
                  type="checkbox"
                  className='gold-tick'
                  checked={newRatePlan.applicableRoomTypeIds.includes(type.id)}
                  onChange={() => handleRoomTypeCheckbox(type.id)}
                />
                {type.name}
              </label>
            ))}
          </div>

          <label>
            <input type="checkbox" className='gold-tick' checked={newRatePlan.active} onChange={(e) => setNewRatePlan({ ...newRatePlan, active: e.target.checked })} />
            Active
          </label>

          <div className="row align-center justify-center gap-1">
            <button type="button" className="login-button" onClick={() => { setIsEditModalOpen(false); resetForm(); setEditingRatePlan(null); }}>Cancel</button>
            <button type="button" className="login-button" onClick={handleUpdateRatePlan}>Update Rate Plan</button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onRequestClose={() => setIsDeleteModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Delete Rate Plan</h2>
        {currentRatePlan && (
          <div>
            <p>
              Are you sure you want to <strong>permanently delete</strong> the rate plan <strong>{currentRatePlan.name}</strong>?<br />              
            </p>
            <div className="row align-center gap-1" style={{ justifyContent: 'center', marginTop: '20px' }}>
              <button className="login-button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
              <button className="login-button delete-btn" onClick={() => handleDelete(currentRatePlan.id)}>
                Confirm
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk Import Modal */}
      <Modal isOpen={isBulkImportModalOpen} onRequestClose={() => { setIsBulkImportModalOpen(false); setFile(null); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Bulk Import Rate Plans</h2>
        <div className="modal-form">
          <div style={{ marginBottom: '5px' }}>
            <label><input type="radio" name="fileType" value="excel" checked={fileType === 'excel'} onChange={handleFileTypeChange} /> Excel</label>&nbsp;&nbsp;
            <label><input type="radio" name="fileType" value="csv" checked={fileType === 'csv'} onChange={handleFileTypeChange} /> CSV</label>
          </div>

          <input type="file" accept={fileType === 'excel' ? '.xlsx,.xls' : '.csv'} onChange={handleFileChange} className="input-field1" />

          <div className="row align-center justify-center gap-15">
            <button className="login-button caps" onClick={() => { setIsBulkImportModalOpen(false); setFile(null); }}>Cancel</button>
            <button className="login-button caps" onClick={processBulkImport} disabled={!file || update}>Import</button>
          </div>

          <div className="import-instructions">
            <h4>Format Instructions:</h4>
            <p><strong>REQUIRED:</strong> name</p>
            <p><strong>OPTIONAL:</strong> code, description, mealPlan, cancellationPolicy, rateAdjustment, rateAdjustmentType (fixed/percentage), active</p>
            <p>Note: Applicable room types are to be selected in the app.</p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default RatePlans;